import React, { FC, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import styles from "./navigation.module.scss";
import { MapSectionType } from "../../../../services/reducers/map";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import { unfocusOnFeature } from "../../../../services/actions/map";
import {
  clearFilters,
  closeSelectedCard,
} from "../../../../services/reducers/filters";
import { clearOpenedCity } from "../../../../services/reducers/cities";
import Button from "../../../ui-kit/button/button";
import { Roles } from "../../../../types/roles";
import ProtectedLink from "./protected-link/protected-link";
import { useTranslation } from "react-i18next";
import ModalCloseMasterPlan from "../../../features/master-plan-modification-portal/modals/modal-close-master-plan/modal-close-master-plan";
import { useNavigationBlocker } from "./navigation-blocker/navigation-blocker";

interface IProps {
  handleScroll: (sectionId: string) => void;
}

export type SectionType =
  | MapSectionType
  | "about"
  | "steps"
  | "solutions"
  | "how-it-works";

const Navigation: FC<IProps> = ({ handleScroll }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [activeSection, setActiveSection] = useState<SectionType | null>(null);
  const dispatch = useAppDispatch();
  const { initialPlot } = useAppSelector((state) => state.multiplePlotList);
  const { clearBlocker, setGlobalBlockers } = useNavigationBlocker();
  const { blockNavigationMasterPlan } = useAppSelector(
    (state) => state.masterPlanModification,
  );
  const isHomePage = pathname === "/";
  const user = useAppSelector((state) => state.user.user);
  const isContractor = user?.roles.includes(Roles.Contractor);

  useEffect(() => {
    dispatch(closeSelectedCard());
    dispatch(clearOpenedCity());
    dispatch(clearFilters({}));
  }, [pathname]);

  useEffect(() => {
    const sections: SectionType[] = [
      "about",
      "solutions",
      "how-it-works",
    ];

    const handleScrollChange = () => {
      let currentSection: SectionType | null = null;

      sections.forEach((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= window.innerHeight / 2) {
            currentSection = section;
          }
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener("scroll", handleScrollChange);

    return () => {
      window.removeEventListener("scroll", handleScrollChange);
    };
  }, []);

  useEffect(() => {
    dispatch(unfocusOnFeature());
  }, [activeSection]);

  const handleClick = (section: SectionType) => {
    setActiveSection(section);
    handleScroll(section);
  };

  useEffect(() => {
    const masterPlanBlocker = {
      condition: blockNavigationMasterPlan,
      modal: <ModalCloseMasterPlan onClose={() => clearBlocker()} />,
    };
    setGlobalBlockers([masterPlanBlocker]);
  }, [blockNavigationMasterPlan]);

  return (
    <nav
      className={`${styles.navigation} ${isHomePage ? styles.homepageNav : styles.internalPageNav}`}
    >
      {pathname === "/" ? (
        <>
          <a href="https://madayn.om" style={{ textDecorationLine: "none" }}>
            <Button type="outline" className={`${styles.navBtn}`}>
              {t("navigation.backToMadayn")}
            </Button>
          </a>
          <Button
            type="outline"
            onClick={() => handleClick("how-it-works")}
            className={`${styles.navBtn} ${activeSection === "how-it-works" ? styles.active : ""}`}
          >
            {t("navigation.howItWorks")}
          </Button>
        </>
      ) : isContractor ? (
        <ProtectedLink to="/repository" style={{ textDecorationLine: "none" }}>
          <Button
            type="outline"
            className={`${styles.navBtn} ${styles.opacity} ${pathname.includes("repository") ? styles.active : ""}`}
          >
            {t("navigation.repository")}
          </Button>
        </ProtectedLink>
      ) : (
        <>
          <ProtectedLink to="/search" style={{ textDecorationLine: "none" }}>
            <Button
              type="outline"
              className={`${styles.navBtn} ${styles.opacity} ${pathname.includes("search") ? styles.active : ""}`}
            >
              {t("navigation.search")}
            </Button>
          </ProtectedLink>
          <ProtectedLink to="/cities" style={{ textDecorationLine: "none" }}>
            <Button
              type="outline"
              className={`${styles.navBtn} ${styles.opacity} ${pathname.includes("cities") ? styles.active : ""}`}
            >
              {t("navigation.cities")}
            </Button>
          </ProtectedLink>
          <ProtectedLink to="/products" style={{ textDecorationLine: "none" }}>
            <Button
              type="outline"
              className={`${styles.navBtn} ${styles.opacity} ${pathname.includes("products") ? styles.active : ""}`}
            >
              {t("navigation.products")}
            </Button>
          </ProtectedLink>
          <ProtectedLink to="/companies" style={{ textDecorationLine: "none" }}>
            <Button
              type="outline"
              className={`${styles.navBtn} ${styles.opacity} ${pathname.includes("companies") ? styles.active : ""}`}
            >
              {t("navigation.companies")}
            </Button>
          </ProtectedLink>
          {initialPlot && !user && (
            <ProtectedLink
              to="/multiplePlotList"
              style={{ textDecorationLine: "none" }}
            >
              <Button
                type="outline"
                className={`${styles.navBtn} ${styles.opacity} ${pathname.includes("multiplePlotList") ? styles.active : ""}`}
              >
                {t("navigation.multiplePlotList")}
              </Button>
            </ProtectedLink>
          )}
          {(user?.roles.includes(Roles.KrookieJournal) ||
            user?.roles.includes(Roles.ReportsJournal) ||
            user?.roles.includes(Roles.Contractor)) && (
            <ProtectedLink
              to="/repository"
              style={{ textDecorationLine: "none" }}
            >
              <Button
                type="outline"
                className={`${styles.navBtn} ${styles.opacity} ${pathname.includes("repository") ? styles.active : ""}`}
              >
                {t("navigation.repository")}
              </Button>
            </ProtectedLink>
          )}
          {user?.roles.includes(Roles.KrookieCreator) && (
            <ProtectedLink
              to="/masterPlan"
              style={{ textDecorationLine: "none" }}
            >
              <Button
                type="outline"
                className={`${styles.navBtn} ${styles.opacity} ${pathname.includes("masterPlan") ? styles.active : ""}`}
              >
                {t("navigation.masterPlanPortal")}
              </Button>
            </ProtectedLink>
          )}
        </>
      )}
    </nav>
  );
};

export default Navigation;
